import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema';

export type { paths, components };

/** The error body every endpoint uses (BUILD_PLAN §7). Switch on `code`; `message` is localised. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Session {
  accessToken: string;
  refreshToken: string;
}

export interface ClientOptions {
  baseUrl: string;
  /** Current tokens, or null when signed out. */
  getSession(): Session | null;
  /** Called with new tokens after a refresh, or null when the session ended. */
  setSession(session: Session | null): void;
  getLanguage(): string;
  /** Stable per-install id; the backend hashes it (device list, risk). */
  deviceId: string;
  channel: 'web' | 'telegram' | 'android';
  fetch?: typeof fetch;
  /**
   * Refresh the player token on a 401 (default). The back office has no refresh token: its
   * server-side session simply ends, so it passes false and is signed out instead.
   */
  refresh?: boolean;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// These issue credentials and are not idempotency-checked by the backend.
const NO_IDEMPOTENCY = /^\/(v1\/auth\/(register|login|telegram|refresh)|admin\/v1\/auth\/(login|mfa))$/;
const NO_REFRESH = /^\/v1\/auth\//;

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Typed API client generated from the backend's OpenAPI spec. It adds auth headers and refreshes
 * the access token once on a 401 (one refresh shared by concurrent requests); sends an
 * Idempotency-Key on every mutation, so a retry of the same request object never applies twice;
 * and turns error bodies into ApiError.
 */
export function createApi(opts: ClientOptions) {
  const doFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  let refreshing: Promise<boolean> | null = null;
  // fetch() consumes a request body, so a retry after refreshing needs an untouched copy.
  const pristine = new WeakMap<Request, Request>();
  // Retries are marked here rather than with a custom header: an extra header triggers a CORS
  // preflight the API may reject, and the retry would then fail as a network error.
  const retried = new WeakSet<Request>();

  async function refresh(): Promise<boolean> {
    const session = opts.getSession();
    if (!session) return false;
    const req = new Request(`${opts.baseUrl}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'accept-language': opts.getLanguage(), 'x-device-id': opts.deviceId, 'x-channel': opts.channel },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    });
    const res = await doFetch(req).catch(() => null);
    if (!res) return false; // offline: keep the session and let the caller see the error
    if (!res.ok) {
      opts.setSession(null);
      return false;
    }
    const body = (await res.json()) as { access_token: string; refresh_token: string };
    opts.setSession({ accessToken: body.access_token, refreshToken: body.refresh_token });
    return true;
  }

  const headers: Middleware = {
    onRequest({ request, schemaPath }) {
      const session = opts.getSession();
      if (session && !request.headers.has('authorization')) request.headers.set('authorization', `Bearer ${session.accessToken}`);
      request.headers.set('accept-language', opts.getLanguage());
      request.headers.set('x-device-id', opts.deviceId);
      request.headers.set('x-channel', opts.channel);
      if (MUTATING.has(request.method) && !NO_IDEMPOTENCY.test(schemaPath) && !request.headers.has('idempotency-key')) {
        request.headers.set('idempotency-key', newIdempotencyKey());
      }
      if (request.body) pristine.set(request, request.clone());
      return request;
    },
    async onResponse({ request, response, schemaPath, options }) {
      if (response.status === 401 && opts.refresh === false && opts.getSession() && !/\/auth\/(login|mfa)$/.test(schemaPath)) {
        opts.setSession(null);
        return response;
      }
      if (response.status === 401 && opts.refresh !== false && opts.getSession() && !NO_REFRESH.test(schemaPath) && !retried.has(request)) {
        refreshing ??= refresh().finally(() => (refreshing = null));
        if (await refreshing) {
          const retry = new Request(pristine.get(request) ?? request, { headers: new Headers(request.headers) });
          retry.headers.set('authorization', `Bearer ${opts.getSession()!.accessToken}`);
          retried.add(retry);
          return (options.fetch ?? doFetch)(retry);
        }
      }
      return response;
    },
  };

  const client = createClient<paths>({ baseUrl: opts.baseUrl, fetch: doFetch });
  client.use(headers);
  return client;
}

export type Api = ReturnType<typeof createApi>;

/** Unwraps an openapi-fetch result: returns data, or throws ApiError. */
export async function call<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await promise;
  } catch {
    throw new ApiError(0, 'NETWORK', 'No connection. Check your internet and try again.');
  }
  if (result.response.ok) return result.data as T;
  const e = (result.error ?? {}) as { code?: string; message?: string; details?: Record<string, unknown> };
  throw new ApiError(result.response.status, e.code ?? 'INTERNAL', e.message ?? 'Something went wrong.', e.details ?? {});
}

export type Schemas = components['schemas'];
